import React, { useState, useEffect, useRef } from "react";
import { useMobileContext } from "../../layouts/mobile";
import { FaPlus, FaPrint, FaEye, FaReceipt, FaUser, FaMoneyBillWave, FaCalendar, FaBuilding, FaSearch, FaChevronDown } from "react-icons/fa";
import { formatLocalDateISO, formatToZMW, rootUrl, sumOfArray, authHeaders } from "../../helpers";
import { useAuth } from "../../context/auth-context";
import axios from "axios";
import { toast } from "react-toastify";
import Modal from "../../components/Modal";
import ReceiptPage from "./receipt";

// Searchable Select Component
const SearchableSelect = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Select an option...", 
  searchPlaceholder = "Search...",
  displayKey = "name",
  valueKey = "id",
  className = "",
  label = "",
  icon = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const selectRef = useRef(null);

  useEffect(() => {
    const filtered = options.filter(option =>
      option[displayKey]?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options, displayKey]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option[valueKey]);
    setIsOpen(false);
    setSearchTerm("");
  };

 

  const selectedOption = options.find(option => option[valueKey] === value);

  return (
    <div className="relative" ref={selectRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {icon && <span className="inline mr-2">{icon}</span>}
          {label}
        </label>
      )}
      <div
        className={`w-full p-3 border border-gray-300 rounded-lg cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-transparent ${className}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <span className={selectedOption ? "text-gray-900" : "text-gray-500"}>
            {selectedOption ? selectedOption[displayKey] : placeholder}
          </span>
          <FaChevronDown className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
            </div>
          </div>
          
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option[valueKey]}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                  onClick={() => handleSelect(option)}
                >
                  {option[displayKey]}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500 text-sm text-center">
                No options found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function Payment() {
  const { allData, groupedData, refreshAllData } = useMobileContext();
  const auth = useAuth();
  const [submitting, setSubmitting] = useState(false);
  
  // State management
  const [activeTab, setActiveTab] = useState("receipts");
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showTransactionPopup, setShowTransactionPopup] = useState(false);
  const [transactionDetails, setTransactionDetails] = useState(null);
  
  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    customer: "",
    collectionCenter: "",
    date: formatLocalDateISO(),
    description: "",
    sender_id: "",
    items: []
  });
  
  // New item form state
  const [newItem, setNewItem] = useState({
    budget: "",
    amount: ""
  });
  
  // Customer form state
  const [customerForm, setCustomerForm] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    address: ""
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  
  // Search and pagination logic
  const sortedReceipts = groupedData.sort((a, b) => new Date(b.date) - new Date(a.date));
  
  // Filter receipts based on search term
  const filteredReceipts = sortedReceipts.filter(receipt => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      receipt.voucher?.toString().toLowerCase().includes(searchLower) ||
      receipt.paye?.toLowerCase().includes(searchLower)
    );
  });
  
  const totalPages = Math.ceil(filteredReceipts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentReceipts = filteredReceipts.slice(startIndex, endIndex);
  
  // Search handler
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };
  
  // Pagination controls
  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  
  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  
  // Add new item to payment form
  const addPaymentItem = () => {
    if (newItem.budget && newItem.amount) {
      setPaymentForm(prev => ({
        ...prev,
        items: [...prev.items, { ...newItem, amount: parseFloat(newItem.amount) }]
      }));
      setNewItem({ budget: "", amount: "" });
    } else {
      toast.error("Please fill in budget and amount fields");
    }
  };
  
  // Remove item from payment form
  const removePaymentItem = (index) => {
    setPaymentForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };
  
  // Update payment form
  const updatePaymentForm = (field, value) => {
    setPaymentForm(prev => ({ ...prev, [field]: value }));
  };
  
  // Update new item form
  const updateNewItem = (field, value) => {
    setNewItem(prev => ({ ...prev, [field]: value }));
  };
  
  // Calculate total amount
  const calculateTotal = () => {
    return paymentForm.items.reduce((sum, item) => sum + (item.amount || 0), 0);
  };
  
  // Update customer form
  const updateCustomerForm = (field, value) => {
    setCustomerForm(prev => ({ ...prev, [field]: value }));
  };
  
  // Submit payment via SchoolPay mobile debit
  const submitPayment = async () => {
    const totalAmount = calculateTotal();

    if (totalAmount <= 0) {
      toast.error("Please add at least one item with amount");
      return;
    }
    if (!paymentForm.customer) {
      toast.error("Please select a customer");
      return;
    }
    if (paymentForm.items.length === 0) {
      toast.error("Please add at least one payment item");
      return;
    }
    if (!paymentForm.sender_id?.trim()) {
      toast.error("Phone number is required for payment");
      return;
    }

    const paymentData = {
      customer_id: paymentForm.customer,
      collection_center: paymentForm.collectionCenter,
      date: paymentForm.date,
      description: paymentForm.description || "Mobile revenue collection",
      sender_id: paymentForm.sender_id.trim(),
      items: paymentForm.items.map((item) => ({
        budget: item.budget,
        amount: item.amount,
        desc: paymentForm.description || "Mobile payment",
      })),
      uid: auth.user?.id,
      cid: auth.user?.cid,
    };

    try {
      setSubmitting(true);
      const response = await axios.post(
        rootUrl("/schoolpay/mobile-payment"),
        paymentData,
        { headers: authHeaders() }
      );

      if (response.data?.status !== 200) {
        toast.error(response.data?.message || "SchoolPay request failed");
        return;
      }

      const sp = response.data?.data?.schoolpay || {};
      const paymentId = response.data?.data?.payment_id;
      setTransactionDetails({
        payment_id: paymentId,
        reference_no: sp.paymentReference || response.data?.data?.external_reference,
        external_reference: response.data?.data?.external_reference,
        operator: "SchoolPay",
        sender_id: paymentForm.sender_id,
        response_description: sp.returnMessage || response.data?.data?.message || sp.status || "Awaiting customer approval on phone",
        amount: totalAmount,
        status: sp.status || "PENDING",
      });
      setShowTransactionPopup(true);
      setShowAddPayment(false);
      setPaymentForm({
        customer: "",
        collectionCenter: "",
        date: formatLocalDateISO(),
        description: "",
        sender_id: "",
        items: [],
      });
      setNewItem({ budget: "", amount: "" });
    } catch (error) {
      console.error("SchoolPay payment error:", error);
      toast.error(
        error?.response?.data?.message || error?.message || "Could not initiate payment"
      );
    } finally {
      setSubmitting(false);
    }
  };
  
  const syncSchoolPayStatus = async (details) => {
    if (!details) return null;
    const refs = [...new Set([details.reference_no, details.external_reference].filter(Boolean))];
    let last = null;
    for (const ref of refs) {
      try {
        const syncRes = await axios.get(
          rootUrl(`/schoolpay/mobile-status/${encodeURIComponent(ref)}`),
          { headers: authHeaders() }
        );
        const data = syncRes.data?.data;
        if (data) {
          last = data;
          if (data.is_paid) {
            return data;
          }
        }
      } catch (e) {
        console.warn("SchoolPay status sync:", e);
      }
    }
    return last;
  };

  const applyStatusSyncToDetails = (details, synced) => {
    if (!details || !synced) return details;
    const spStatus = synced.schoolpay?.status;
    const localStatus = synced.local?.status;
    const checkError = synced.check_error;
    let response_description = details.response_description;
    if (synced.is_paid) {
      response_description = "Payment confirmed";
    } else if (checkError) {
      response_description = `Could not reach SchoolPay: ${checkError}`;
    } else if (spStatus) {
      response_description = `SchoolPay: ${spStatus}`;
    } else if (localStatus) {
      response_description = `Recorded as ${localStatus}`;
    } else {
      response_description = "Still pending — waiting on customer or SchoolPay";
    }
    return {
      ...details,
      status: synced.is_paid ? "PAID" : localStatus || spStatus || details.status,
      response_description,
    };
  };

  const closeTransactionPopup = async () => {
    setShowTransactionPopup(false);
    setTransactionDetails(null);
    if (refreshAllData) {
      await refreshAllData();
    }
  };

  useEffect(() => {
    if (!showTransactionPopup || !transactionDetails) return undefined;

    let cancelled = false;
    const poll = async () => {
      if (transactionDetails.status === "PAID") return;
      const synced = await syncSchoolPayStatus(transactionDetails);
      if (cancelled || !synced?.is_paid) return;
      setTransactionDetails((prev) => applyStatusSyncToDetails(prev, synced));
      toast.success("Payment confirmed.");
      await closeTransactionPopup();
    };

    poll();
    const timer = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [showTransactionPopup, transactionDetails?.reference_no, transactionDetails?.external_reference]);

  // Submit customer
  const submitCustomer = async () => {
    try {
      if (!customerForm.name || !customerForm.email) {
        toast.error("Please fill in required fields");
        return;
      }
      
      const response = await axios.post(rootUrl('/addCustomer'), {
        ...customerForm,
        uid: auth.user?.id,
        type: 3
      });
      
      if (response.data.status === 200) {
        toast.success("Customer added successfully!");
        setShowAddCustomer(false);
        
        setCustomerForm({ name: "", email: "", phoneNumber: "", address: "" });
        // Refresh data or handle success
      } else {
        toast.error(response.data.message || "Failed to add customer");
      }
    } catch (error) {
      console.error("Error adding customer:", error);
      toast.error("Error adding customer. Please try again.");
    }
  };
  
  // View receipt
  const viewReceipt = (receipt) => {
    setSelectedReceipt(receipt);
    setShowReceiptModal(true);
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-600 text-white p-6 rounded-2xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Payment Management</h1>
            <p className="text-blue-100">Manage receipts and payments</p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowAddCustomer(true)}
              className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl hover:bg-white/30 transition-all duration-300"
            >
              <FaUser className="inline mr-2" /> Add Customer
            </button>
            <button
              onClick={() => setShowAddPayment(true)}
              className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl hover:bg-white/30 transition-all duration-300"
            >
              <FaPlus className="inline mr-2" /> Add Payment
            </button>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="flex border-b">
          <button
            className={`flex-1 py-4 px-6 text-center font-semibold transition-all duration-300 ${
              activeTab === "receipts" 
                ? "shadow-lg" 
                : "text-gray-600 hover:bg-gray-50"
            }`}
            onClick={() => setActiveTab("receipts")}
          >
            <FaReceipt className="inline mr-2" /> Receipts
          </button>
        </div>
        
        {/* Receipts Tab */}
        {activeTab === "receipts" && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Recent Receipts</h2>
              <span className="text-sm text-gray-500">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredReceipts.length)} of {filteredReceipts.length}
              </span>
            </div>
            
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by receipt number or customer name..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={() => handleSearch("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            
            {/* Receipts List */}
            <div className="space-y-3">
              {currentReceipts.length > 0 ? (
                currentReceipts.map((receipt) => (
                  <div
                    key={receipt.voucher}
                    className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-all duration-300"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                            <FaReceipt className="text-white text-sm" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-800">#{receipt.voucher}</h3>
                            <p className="text-sm text-gray-600">{receipt.paye}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">
                            {new Date(receipt.date).toLocaleDateString()}
                          </span>
                          <span className={`font-bold text-lg ${
                            receipt.type === 'income' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            K{formatToZMW(sumOfArray(receipt.transactions.map(res => parseFloat(res.amount))))}
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => viewReceipt(receipt)}
                          className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          <FaEye className="text-sm" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedReceipt(receipt);
                            setShowReceiptModal(true);
                          }}
                          className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                          <FaPrint className="text-sm" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <FaReceipt className="text-6xl text-gray-300 mx-auto mb-4" />
                  {searchTerm ? (
                    <>
                      <h3 className="text-lg font-semibold text-gray-600 mb-2">No receipts found</h3>
                      <p className="text-gray-500 mb-4">No receipts match your search for "{searchTerm}"</p>
                      <button
                        onClick={() => handleSearch("")}
                        className="text-blue-500 hover:text-blue-600 font-medium"
                      >
                        Clear search
                      </button>
                    </>
                  ) : (
                    <>
                      <h3 className="text-lg font-semibold text-gray-600 mb-2">No receipts found</h3>
                      <p className="text-gray-500">Start by adding a payment to generate receipts</p>
                    </>
                  )}
                </div>
              )}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  ← Previous
                </button>
                
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                
                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Add Payment Modal */}
      {showAddPayment && (
        <Modal title="Add Payment" onClose={() => setShowAddPayment(false)}>
          <div className="space-y-6 pb-20 max-h-[80vh] overflow-y-auto">
            {/* Customer Selection */}
            <SearchableSelect
              options={allData?.suppliers.filter(a=>a.type == 3) || []}
              value={paymentForm.customer}
              onChange={(value) => updatePaymentForm('customer', value)}
              placeholder="Choose a customer..."
              searchPlaceholder="Search customers..."
              displayKey="name"
              valueKey="id"
              label="Select Customer"
              icon={<FaUser />}
            />
            
            {/* Collection Center */}
            <SearchableSelect
              options={allData?.cc.map(a=>({id:a.id, name:a.collection_name})) || []}
              value={paymentForm.collectionCenter}
              onChange={(value) => updatePaymentForm('collectionCenter', value)}
              placeholder="Select collection center..."
              searchPlaceholder="Search collection centers..."
              displayKey="name"
              valueKey="id"
              label="Collection Center"
              icon={<FaBuilding />}
            />
            
            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaCalendar className="inline mr-2" /> Payment Date
              </label>
              <input
                type="date"
                value={paymentForm.date}
                onChange={(e) => updatePaymentForm('date', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Payment Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Description
              </label>
              <textarea
                value={paymentForm.description}
                onChange={(e) => updatePaymentForm('description', e.target.value)}
                placeholder="Enter payment description..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
              />
            </div>
            
            {/* Sender ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="text"
                value={paymentForm.sender_id}
                onChange={(e) => updatePaymentForm('sender_id', e.target.value)}
                placeholder="+2609xxxxxxxx"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Add New Item Form */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                <FaMoneyBillWave className="inline mr-2" /> Add Payment Item
              </h3>
              
              <div className="grid grid-cols-1 gap-3">
                <SearchableSelect
                  options={allData?.budget?.map(budget => ({
                    id: budget.id,
                    name: `${budget.coa} ${allData?.coa?.find(a => a.account === budget.coa)?.name || ''}`.trim(),
                    coa: budget.coa
                  })) || []}
                  value={newItem.budget}
                  onChange={(value) => updateNewItem('budget', value)}
                  placeholder="Select budget category..."
                  searchPlaceholder="Search budget categories..."
                  displayKey="name"
                  valueKey="id"
                  label="Budget Category"
                />
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (K)</label>
                  <input
                    type="number"
                    value={newItem.amount}
                    onChange={(e) => updateNewItem('amount', e.target.value)}
                    placeholder="0.00"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <button
                  onClick={addPaymentItem}
                  className="w-full bg-blue-500 text-white py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
                >
                  <FaPlus className="inline mr-2" /> Add Item
                </button>
              </div>
            </div>
            
            {/* Items Table */}
            {paymentForm.items.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b">
                  <h3 className="text-lg font-semibold text-gray-800">Payment Items</h3>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Budget Category</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {paymentForm.items.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {allData?.budget?.find(b => b.id == item.budget)?.coa || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                            K{formatToZMW(item.amount)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => removePaymentItem(index)}
                              className="text-red-500 hover:text-red-700 text-sm font-medium"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                          Total Amount:
                        </td>
                        <td className="px-4 py-3 text-right text-lg font-bold text-blue-600">
                          K{formatToZMW(calculateTotal())}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
            
            {/* Submit Button */}
            <button
              onClick={submitPayment}
              disabled={submitting}
              className="w-full bg-green-500 text-white py-3 rounded-lg font-semibold hover:bg-green-600 transition-all duration-300 disabled:opacity-60"
            >
              {submitting ? "Sending ..." : "Request Payment"}
            </button>
          </div>
        </Modal>
      )}
      
      {/* Add Customer Modal */}
      {showAddCustomer && (
        <Modal title="Add Customer" onClose={() => setShowAddCustomer(false)}>
          <div className="space-y-4 pb-20 max-h-[80vh] overflow-y-auto">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
              <input
                type="text"
                value={customerForm.name}
                onChange={(e) => updateCustomerForm('name', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter customer name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
              <input
                type="email"
                value={customerForm.email}
                onChange={(e) => updateCustomerForm('email', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter email address"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
              <input
                type="tel"
                value={customerForm.phoneNumber}
                onChange={(e) => updateCustomerForm('phoneNumber', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter phone number"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
              <textarea
                value={customerForm.address}
                onChange={(e) => updateCustomerForm('address', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter address"
                rows={3}
              />
            </div>
            
            <button
              onClick={submitCustomer}
              className="w-full bg-green-500 text-white py-3 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 transition-all duration-300"
            >
              Add Customer
            </button>
          </div>
        </Modal>
      )}
      
      {/* Receipt Modal */}
      {showReceiptModal && selectedReceipt && (
        <Modal title="Receipt" onClose={() => setShowReceiptModal(false)}>
          <ReceiptPage 
            receipt={selectedReceipt} 
            council={allData?.council?.[0]?.name} 
          />
        </Modal>
      )}

      {/* Transaction Initiated Popup */}
      {showTransactionPopup && transactionDetails && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 pb-20">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl max-h-[80vh] overflow-y-auto">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaMoneyBillWave className="text-2xl text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Request Sent</h2>
              <p className="text-gray-600">Customer should approve the debit prompt on their phone</p>
            </div>

            {/* Transaction Details */}
            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 p-4 rounded-xl">
                <div className="">
                  <div>
                    <p className="text-sm text-gray-600">Payment Reference</p>
                    <p className="font-semibold text-gray-900 break-all">{transactionDetails.reference_no || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">External Ref</p>
                    <p className="font-semibold text-gray-900 text-xs break-all">{transactionDetails.external_reference || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Amount</p>
                    <p className="font-semibold text-green-600">K{formatToZMW(transactionDetails.amount)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Operator</p>
                    <p className="font-semibold text-gray-900">{transactionDetails.operator}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Sender ID</p>
                    <p className="font-semibold text-gray-900">{transactionDetails.sender_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <p className="font-semibold text-blue-600">{transactionDetails.response_description}</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500 text-center mb-4">
              Checking with SchoolPay every few seconds. If the customer already paid, leave this open or tap Check status.
            </p>

            <div className="space-y-2">
              <button
                type="button"
                onClick={async () => {
                  const synced = await syncSchoolPayStatus(transactionDetails);
                  if (!synced) {
                    toast.error("Could not check payment status. Check your connection and try again.");
                    return;
                  }
                  if (synced.is_paid) {
                    toast.success("Payment confirmed.");
                    await closeTransactionPopup();
                    return;
                  }
                  setTransactionDetails((prev) => applyStatusSyncToDetails(prev, synced));
                  toast.info(
                    synced?.check_error
                      ? "Could not verify with SchoolPay right now. Try again in a moment."
                      : "Still pending on SchoolPay. If the customer already paid, wait and tap Check status again."
                  );
                }}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-700 transition-all"
              >
                Check status
              </button>
              <button
                type="button"
                onClick={closeTransactionPopup}
                className="w-full text-gray-600 py-2 text-sm hover:text-gray-900"
              >
                Close (payment may still complete in background)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
